/**
 * AlertsList
 *
 * US-24-GR-27 — Paginated list of raised threshold alerts on QueueHealthPage.
 * Shows severity/type badges, raised/recovered/acknowledged times, and an
 * Acknowledge button on open alerts.
 */

import { useCallback, useEffect, useState, startTransition, type ReactNode } from 'react';
import { RefreshCw, Bell } from 'lucide-react';
import {
  getAlerts,
  acknowledgeAlert,
  type ThresholdAlert,
  type AlertType,
  type AlertSeverity,
} from '../api/orchestratorClient';
import { AbbrLabel } from './AbbrLabel';

const PAGE_SIZE = 20;

const TYPE_LABELS: Record<AlertType, ReactNode> = {
  QueueDepth: 'Queue Depth',
  DlqDepth: <><AbbrLabel term="DLQ" /> Depth</>,
  SuccessRateFloor: 'Success Rate Floor',
};

const SEVERITY_CLASSES: Record<AlertSeverity, string> = {
  Warn: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  Alert: 'text-red-400 bg-red-500/10 border-red-500/20',
};

function formatTs(ts: string | null | undefined): string {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

interface AlertsListProps {
  /** Bumped by the parent to force a refetch (e.g. on a live SignalR ThresholdAlert event). */
  refreshToken?: number;
}

export function AlertsList({ refreshToken }: AlertsListProps) {
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState<ThresholdAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ackingId, setAckingId] = useState<number | null>(null);

  const load = useCallback(async (targetPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAlerts({ page: targetPage, pageSize: PAGE_SIZE });
      setItems(result.items);
      setTotal(result.total);
      setPage(result.page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    startTransition(() => {
      void load(1);
    });
    // Reset to page 1 whenever a fresh alert is signalled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  async function handleAcknowledge(alertId: number) {
    setAckingId(alertId);
    setError(null);
    try {
      await acknowledgeAlert(alertId);
      await load(page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to acknowledge alert');
    } finally {
      setAckingId(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section aria-label="Threshold alerts">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-zinc-400 flex items-center gap-1.5">
          <Bell className="w-3.5 h-3.5" />
          Alerts{total ? ` (${total})` : ''}
        </p>
      </div>

      {error && (
        <div role="alert" className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-xs text-red-300 mb-2">
          {error}
        </div>
      )}

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        {loading && items.length === 0 ? (
          <div className="p-4 text-xs text-zinc-500 flex items-center gap-2">
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center text-xs text-zinc-500">No alerts raised.</div>
        ) : (
          <ul>
            {items.map((alert) => (
              <li key={alert.id} className="border-b border-zinc-800/50 last:border-0">
                <div className="flex items-center gap-3 px-4 py-2.5 min-h-[48px] flex-wrap">
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded border font-medium flex-shrink-0 ${SEVERITY_CLASSES[alert.severity]}`}
                  >
                    {alert.severity}
                  </span>
                  <span className="text-xs bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-300 flex-shrink-0">
                    {TYPE_LABELS[alert.alertType] ?? alert.alertType}
                  </span>
                  <span className="text-xs text-zinc-300 flex-1 min-w-[160px]">{alert.message}</span>
                  <span className="text-xs font-mono text-zinc-500 flex-shrink-0">
                    {alert.metricValue} / {alert.thresholdValue}
                  </span>
                  <time dateTime={alert.raisedAtUtc} className="text-xs text-zinc-500 flex-shrink-0">
                    raised {formatTs(alert.raisedAtUtc)}
                  </time>
                  {alert.recoveredAtUtc && (
                    <span className="text-xs text-emerald-400 flex-shrink-0">
                      recovered {formatTs(alert.recoveredAtUtc)}
                    </span>
                  )}

                  {alert.isOpen ? (
                    alert.acknowledgedAtUtc ? (
                      <span className="text-xs text-zinc-500 flex-shrink-0">
                        acked by {alert.acknowledgedBy ?? '—'}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void handleAcknowledge(alert.id)}
                        disabled={ackingId === alert.id}
                        aria-label={`Acknowledge alert ${alert.id}`}
                        className="flex-shrink-0 min-h-[48px] px-3 py-1 rounded-lg border border-violet-500/50 text-violet-400 hover:bg-violet-500/10 hover:text-violet-300 hover:border-violet-400 text-xs font-medium transition-colors disabled:opacity-50"
                      >
                        {ackingId === alert.id ? 'Acknowledging…' : 'Acknowledge'}
                      </button>
                    )
                  ) : (
                    <span className="text-xs text-zinc-600 flex-shrink-0">closed</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-2">
          <button
            type="button"
            onClick={() => void load(page - 1)}
            disabled={page <= 1 || loading}
            className="px-3 py-1.5 text-xs rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 disabled:opacity-40 transition-colors min-h-[48px]"
          >
            Previous
          </button>
          <span className="text-xs text-zinc-500">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => void load(page + 1)}
            disabled={page >= totalPages || loading}
            className="px-3 py-1.5 text-xs rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 disabled:opacity-40 transition-colors min-h-[48px]"
          >
            Next
          </button>
        </div>
      )}
    </section>
  );
}

export default AlertsList;
